
/**
 * Module dependencies.
 */

import color from 'color';

/**
 * Blue palette.
 */

const bluePalette = {
  blue100: '#f2f7ff',
  blue200: '#d1e5ff',
  blue300: '#bdd9ff',
  blue400: '#a8ceff',
  blue500: '#3366ff',
  blue600: '#254edb',
  blue700: '#1939b7',
  blue800: '#102693',
  blue900: '#091a7a'
};

/**
 * Gray palette.
 */

const grayPalette = {
  gray100: '#f9fafa',
  gray200: '#f1f2f3',
  gray300: '#e3e6e9',
  gray400: '#d3d6dc',
  gray500: '#98a2af',
  gray600: '#778393',
  gray700: '#667281',
  gray800: '#535e6d',
  gray900: '#404a56'
};

/**
 * Indigo palette.
 */

const indigoPalette = {
  indigo100: '#dfdefc',
  indigo200: '#c0bffa',
  indigo300: '#9d9cf2',
  indigo400: '#817fe6',
  indigo500: '#5957d6',
  indigo600: '#413fb8',
  indigo700: '#2d2b9a',
  indigo800: '#1c1b7c',
  indigo900: '#111066'
};

/**
 * Night blue palette.
 */

const nightBluePalette = {
  nightBlue100: '#d1e6f5',
  nightBlue200: '#a6ccec',
  nightBlue300: '#6f9bc6',
  nightBlue400: '#39597f',
  nightBlue500: '#122744',
  nightBlue600: '#0d1e3a',
  nightBlue700: '#091630',
  nightBlue800: '#050f27',
  nightBlue900: '#030a20'
};

/**
 * Purple palette.
 */

const purplePalette = {
  purple100: '#faddfd',
  purple200: '#f2bdfb',
  purple300: '#e299f5',
  purple400: '#cd7deb',
  purple500: '#b054de',
  purple600: '#8b3dbe',
  purple700: '#692a9f',
  purple800: '#4b1a80',
  purple900: '#35106a'
};

/**
 * Danger palette.
 */

const dangerPalette = {
  danger100: '#fde7e7',
  danger200: '#fbd5d5',
  danger300: '#f9c2c2',
  danger400: '#f7b0b0',
  danger500: '#f13b3b',
  danger600: '#cf2b39',
  danger700: '#ad1d36',
  danger800: '#8b1232',
  danger900: '#730b2f'
};

/**
 * Info palette.
 */

const infoPalette = {
  info100: '#f2f7ff',
  info200: '#d1e5ff',
  info300: '#bdd9ff',
  info400: '#a8ceff',
  info500: '#3366ff',
  info600: '#254ebd',
  info700: '#1939b7',
  info800: '#102693',
  info900: '#091a7a'
};

/**
 * Success palette.
 */

const successPalette = {
  success100: '#dff9e5',
  success200: '#c0f2cb',
  success300: '#8ee7a3',
  success400: '#56d274',
  success500: '#33c758',
  success600: '#21983f',
  success700: '#1e7734',
  success800: '#1c5f2e',
  success900: '#194e28'
};

/**
 * Warning palette.
 */

const warningPalette = {
  warning100: '#fff2cc',
  warning200: '#ffe199',
  warning300: '#ffcc66',
  warning400: '#ffb73f',
  warning500: '#ff9500',
  warning600: '#db7600',
  warning700: '#b75b00',
  warning800: '#934300',
  warning900: '#7a3200'
};

/**
 * Palette.
 */

const palette = {
  ...bluePalette,
  ...dangerPalette,
  ...grayPalette,
  ...infoPalette,
  ...indigoPalette,
  ...nightBluePalette,
  ...purplePalette,
  ...successPalette,
  ...warningPalette,
  black: '#000000',
  facebook: '#1877f2',
  google: '#4285f4',
  white: '#ffffff'
};

/**
 * Export `hslColor`.
 */

export function hslColor(colorName: keyof typeof palette, lightness: number): string {
  return color(palette[colorName]).lightness(lightness).toString();
}

/**
 * Export `toHex`.
 */

export function toHex(colorValue: string): string {
  return color(colorValue).hex().toString();
}

/**
 * Export `colors`.
 */

export const colors = {
  ...palette,
  danger: dangerPalette.danger500,
  gray: grayPalette.gray500,
  info: infoPalette.info500,
  primary: bluePalette.blue500,
  secondary: nightBluePalette.nightBlue500,
  success: successPalette.success500,
  warning: warningPalette.warning500
};
